import { useDebounceHook } from "@/hooks/use-debounce";
import type { GetRolesFilters } from "@/queries/roles";
import type { GetTagsParams } from "@/queries/tags/tags.api";
import type { UsersFiltersProps } from "@/queries/users/users.api";
import type {
  CatalogSearchItem,
  CommonInfiniteQueryType,
  SearchType,
} from "@/types/common/filters";
import { searchTypesEnum } from "@/types/common/filters";
import type {
  ApiRoleInfiniteResponseType,
  RolePageData,
  RoleType,
} from "@/types/entities/roles";
import type {
  ApiTagsInfiniteResponseType,
  TagType,
} from "@/types/entities/tags";
import type {
  ApiUserInfiniteResponseType,
  UserPageData,
  UserType,
} from "@/types/entities/users";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Icon,
} from "@elaniintc/goes-design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

type EntityType =
  | UserType
  | RoleType
  | TagType
  | CatalogSearchItem
  | { id: string; name: string };

interface FilterSearchProps {
  title: string;
  searchField: string;
  searchType: SearchType;
  initialSearchText: string;
  useQueryHook: CommonInfiniteQueryType;
  selected: EntityType[] | [];
  onUpdateSearch: (
    type: "remove" | "add",
    value: UserType | RoleType | TagType | CatalogSearchItem,
  ) => void;
  onUpdateSearchFromResult?: (
    value: UserType[] | RoleType[] | CatalogSearchItem[],
  ) => void;
  result?: string;
}

type SearchTypeMap = {
  [searchTypesEnum.user]: {
    response: ApiUserInfiniteResponseType;
    filters: UsersFiltersProps;
  };
  [searchTypesEnum.roles]: {
    response: ApiRoleInfiniteResponseType;
    filters: GetRolesFilters;
  };
  [searchTypesEnum.tags]: {
    response: ApiTagsInfiniteResponseType;
    filters: GetTagsParams;
  };
  [searchTypesEnum.positions]: {
    response: ApiCatalogInfiniteResponseType;
    filters: CatalogFilters;
  };
  [searchTypesEnum.institutions]: {
    response: ApiCatalogInfiniteResponseType;
    filters: CatalogFilters;
  };
};

type CatalogFilters = {
  search?: string;
};

type ApiCatalogInfiniteResponseType = {
  data: CatalogSearchItem[];
  meta?: {
    current_page?: number;
    last_page?: number;
  };
};

const getListType = (searchType: string) => {
  if (searchType === searchTypesEnum.user) {
    return "usuarios";
  }
  if (searchType === searchTypesEnum.roles) {
    return "roles";
  }
  if (searchType === searchTypesEnum.tags) {
    return "etiquetas";
  }
  if (searchType === searchTypesEnum.positions) {
    return "cargos";
  }
  if (searchType === searchTypesEnum.institutions) {
    return "instituciones";
  }
  return "unknown";
};

export default function FilterSearch({
  title,
  initialSearchText,
  searchField,
  searchType,
  selected,
  result,
  useQueryHook,
  onUpdateSearch,
  onUpdateSearchFromResult,
}: FilterSearchProps) {
  const [searchText, setSearchText] = useState(initialSearchText);
  const debouncedSearchText = useDebounceHook(searchText, 500);
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { ref: loaderRef, inView } = useInView();

  const queryParams = useMemo(() => {
    if (debouncedSearchText.length > 3) {
      return {
        [searchField]: debouncedSearchText,
      };
    }
    return {};
  }, [debouncedSearchText, searchField]);

  const typedHook = useQueryHook as CommonInfiniteQueryType<
    SearchTypeMap[typeof searchType]["response"],
    SearchTypeMap[typeof searchType]["filters"]
  >;

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } = typedHook(
    {
      ...queryParams,
      per_page: 80,
      processes: "*",
    },
  );

  useEffect(() => {
    const defaultResults = result?.split(",") || [];
    let newItems: UserType[] | RoleType[] | CatalogSearchItem[] = [];
    if (selected.length === 0 && defaultResults.length > 0) {
      if (!(data && "pages" in data)) return;

      if (searchType === searchTypesEnum.user) {
        const userItems: UserType[] = [];
        data?.pages.forEach((page) => {
          page.data.forEach((item) => {
            if (defaultResults.includes(item.id)) {
              if (item) {
                userItems.push(item as UserType);
              }
            }
          });
        });
        newItems = userItems;
      } else if (searchType === searchTypesEnum.roles) {
        const roleItems: RoleType[] = [];
        data?.pages.forEach((page) => {
          page.data.forEach((item) => {
            if (defaultResults.includes(item.id)) {
              if (item) {
                roleItems.push(item as RoleType);
              }
            }
          });
        });
        newItems = roleItems;
      }
      if (
        searchType === searchTypesEnum.positions ||
        searchType === searchTypesEnum.institutions
      ) {
        const catalogItems: CatalogSearchItem[] = [];
        (data?.pages as ApiCatalogInfiniteResponseType[] | undefined)?.forEach(
          (page) => {
            page.data.forEach((item) => {
              if (defaultResults.includes(item.id) && item) {
                catalogItems.push(item as CatalogSearchItem);
              }
            });
          },
        );
        newItems = catalogItems;
      }
    }
    if (newItems.length > 0) {
      onUpdateSearchFromResult?.(newItems);
    }
  }, [data, selected, result, onUpdateSearchFromResult, searchType]);

  const _data = useMemo(() => {
    if (!data) return [];
    if (!("pages" in data)) return [];

    if (searchType === searchTypesEnum.user) {
      const newData = (data.pages as UserPageData[])
        .flatMap((page) => page.data)
        .map((item) => {
          return {
            ...item,
            roles: Array.isArray(item.user_roles) ? item.user_roles : [],
          } as UserType;
        });

      return newData;
    }

    if (searchType === searchTypesEnum.roles) {
      const newData = (data.pages as RolePageData[])
        .flatMap((page) => page.data)
        .filter((field) => {
          if (selected.some((u) => u.id === field.id)) {
            return false;
          }
          return true;
        })
        .map((item) => ({
          ...item,
        }));

      return newData;
    }

    if (searchType === searchTypesEnum.tags) {
      const newData = (data.pages as ApiTagsInfiniteResponseType["pages"])
        .flatMap((page) => page.data)
        .map((item) => ({
          ...item,
        }));
      return newData;
    }

    if (
      searchType === searchTypesEnum.positions ||
      searchType === searchTypesEnum.institutions
    ) {
      const newData = (data.pages as ApiCatalogInfiniteResponseType[])
        .flatMap((page) => page.data)
        .filter((field) => {
          if (selected.some((u) => u.id === field.id)) {
            return false;
          }
          return true;
        })
        .map((item) => ({
          id: item.id,
          name: item.name || item.nombre || "-",
          nombre: item.nombre,
          description: item.description,
          activo: item.activo,
        }));
      return newData;
    }

    return [];
  }, [data, selected, searchType]);

  const handleSelectUser = (
    user: UserType,
    action: "add" | "remove" = "add",
  ) => {
    onUpdateSearch(action, user);
    setIsOpen(false);
  };

  useEffect(() => {
    if (inView && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetching, fetchNextPage]);

  useEffect(() => {
    if (
      isOpen &&
      _data.length === 0 &&
      !isLoading &&
      !isFetching &&
      debouncedSearchText.length > 3
    ) {
      fetchNextPage();
    }
  }, [
    isOpen,
    _data.length,
    isLoading,
    isFetching,
    debouncedSearchText,
    fetchNextPage,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderDropdownContent = () => {
    if (isLoading) {
      return (
        <div className="w-full h-32 flex items-center justify-center">
          <Icon
            type="far"
            name="spinner-third"
            className="text-on-disabled animate-spin text-2xl"
          />
        </div>
      );
    }

    if (_data.length === 0) {
      return (
        <div className="w-full h-[210px] flex items-center justify-center">
          <span className="text-body-quiet font-normal">
            No hay más resultados
          </span>
        </div>
      );
    }

    return (
      <>
        <ul className="px-2 min-h-32">
          {_data.map((_entity) => {
            if (searchType === searchTypesEnum.user) {
              const entity = _entity as UserType;
              const isSelected = selected.some((u) => u.id === entity.id);
              return (
                <li
                  key={entity.id}
                  className={cn(
                    "flex items-center justify-between gap-3 p-2 hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer",
                    isSelected && "bg-neutral-object-accent-quiet",
                  )}
                  onClick={(event) => {
                    if ((window?.getSelection()?.toString()?.length || 0) > 0) {
                      event.stopPropagation();
                      return;
                    }
                    handleSelectUser(
                      entity as UserType,
                      isSelected ? "remove" : "add",
                    );
                  }}
                >
                  <Avatar size="2xs">
                    <AvatarFallback color="action">
                      {entity.name ? `${entity.name.charAt(0)}` : ""}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 flex items-center gap-2 text-base leading-3xl truncate">
                    <span className="text-body font-semibold whitespace-nowrap">
                      {`${entity.name || entity.first_name} ${entity.last_name}`}
                    </span>
                    <span className="text-body-quiet font-normal truncate">
                      {entity.email || "-"}
                    </span>
                  </div>
                  {!isSelected && (
                    <div className="pl-3">
                      <Icon
                        type="far"
                        name="plus"
                        className="text-body text-base leading-3xl"
                      />
                    </div>
                  )}
                </li>
              );
            }

            if (searchType === searchTypesEnum.roles) {
              const entity = _entity as RoleType;
              return (
                <li
                  key={entity.id}
                  className="flex items-center justify-between gap-3 p-2 hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer"
                  onClick={(event) => {
                    if ((window?.getSelection()?.toString()?.length || 0) > 0) {
                      event.stopPropagation();
                      return;
                    }
                    onUpdateSearch("add", entity as RoleType);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex-1 flex flex-col items-start text-base leading-3xl truncate">
                    <span className="text-body font-semibold whitespace-nowrap">
                      {entity.name}
                    </span>
                    <span className="text-body-quiet font-normal truncate text-xs">
                      {entity.description || "-"}
                    </span>
                  </div>
                  <div className="pl-3">
                    <Icon
                      type="far"
                      name="plus"
                      className="text-body text-base leading-3xl"
                    />
                  </div>
                </li>
              );
            }

            if (searchType === searchTypesEnum.tags) {
              const entity = _entity as TagType;
              const isSelected = selected.some((tag) => tag.id === entity.id);
              const imageUrl =
                entity.files && entity.files.length > 0
                  ? entity.files[0].url
                  : null;
              return (
                <li
                  key={entity.id}
                  className={cn(
                    "flex items-center justify-between gap-3 p-2 hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer",
                    isSelected && "bg-neutral-object-accent-quiet",
                  )}
                  onClick={(event) => {
                    if ((window?.getSelection()?.toString()?.length || 0) > 0) {
                      event.stopPropagation();
                      return;
                    }
                    onUpdateSearch(
                      isSelected ? "remove" : "add",
                      entity as TagType,
                    );
                    setIsOpen(false);
                  }}
                >
                  <div className=" w-full flex gap-2 items-center truncate">
                    <Avatar size="xs" shape="square">
                      <AvatarImage src={imageUrl || ""} alt={entity.name} />
                      <AvatarFallback
                        color="neutral"
                        shape="square"
                        type="icon"
                      >
                        <Icon type="fas" name="tag" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 flex flex-col items-start text-base leading-3xl truncate">
                      <span className="text-body font-semibold whitespace-nowrap">
                        {entity.name}
                      </span>
                      <p className="text-body-quiet font-normal truncate text-xs">
                        {entity.description || "-"}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="pl-3">
                      <Icon
                        type="far"
                        name="check"
                        className="text-body text-base leading-3xl"
                      />
                    </div>
                  )}
                </li>
              );
            }

            if (
              searchType === searchTypesEnum.positions ||
              searchType === searchTypesEnum.institutions
            ) {
              const entity = _entity as CatalogSearchItem;
              return (
                <li
                  key={entity.id}
                  className="flex items-center justify-between gap-3 p-2 hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer"
                  onClick={(event) => {
                    if ((window?.getSelection()?.toString()?.length || 0) > 0) {
                      event.stopPropagation();
                      return;
                    }
                    onUpdateSearch("add", entity as CatalogSearchItem);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex-1 flex flex-col items-start text-base leading-3xl truncate">
                    <span className="text-body font-semibold whitespace-nowrap">
                      {entity.name || entity.nombre || "-"}
                    </span>
                    <span className="text-body-quiet font-normal truncate text-xs">
                      {entity.description || "-"}
                    </span>
                  </div>
                  <div className="pl-3">
                    <Icon
                      type="far"
                      name="plus"
                      className="text-body text-base leading-3xl"
                    />
                  </div>
                </li>
              );
            }

            return null;
          })}
        </ul>
        {hasNextPage && (
          <div
            ref={loaderRef}
            className="w-full flex items-center justify-center p-3"
          >
            <Icon
              type="far"
              name="spinner-third"
              className="text-on-disabled animate-spin text-xl"
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col space-y-6 p-6">
      <div className="flex justify-between">
        <h5 className="text-heading text-xl font-semibold">{title}</h5>
      </div>
      <div className="flex flex-col space-y-0.5">
        <div className="flex space-x-2.5">
          <div className="h-[60px] relative w-full tablet:min-w-[356px]">
            <div className="relative">
              <div className="relative bg-neutral-object-accent-silent border border-neutral rounded-lg py-1.5 px-3">
                <Icon
                  type="far"
                  name="search"
                  className="absolute left-2.5 top-1/2 transform -translate-y-1/2 font-normal text-body text-base leading-3xl"
                />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={`Buscar ${(() => {
                    switch (searchType) {
                      case searchTypesEnum.user:
                        return "usuarios";
                      case searchTypesEnum.roles:
                        return "roles";
                      case searchTypesEnum.tags:
                        return "etiquetas";
                      case searchTypesEnum.positions:
                        return "cargos";
                      case searchTypesEnum.institutions:
                        return "instituciones";
                      default:
                        return "unknown";
                    }
                  })()}...`}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => setIsOpen(true)}
                  className="w-full pl-[34px] text-lg font-normal bg-transparent border-none outline-none focus:ring-0 text-body placeholder:text-body-quiet"
                />
              </div>
            </div>

            {isOpen && (
              <div
                ref={dropdownRef}
                className="absolute tablet:min-w-[356px] top-12 left-0 right-0 bg-neutral-object border border-neutral-quiet rounded-md shadow-lg py-2 z-50"
              >
                <div ref={scrollRef} className="max-h-[220px] overflow-auto ">
                  {(searchType === searchTypesEnum.user ||
                    searchType === searchTypesEnum.roles ||
                    searchType === searchTypesEnum.tags ||
                    searchType === searchTypesEnum.positions ||
                    searchType === searchTypesEnum.institutions) &&
                    renderDropdownContent()}
                </div>
              </div>
            )}
            <div className="max-h-[284px] overflow-y-auto space-y-0.5 mt-6">
              {selected.length > 0 ? (
                <>
                  {searchType === searchTypesEnum.user &&
                    selected.map((user) => (
                      <UserCard
                        user={user as UserType}
                        onUpdateSearch={onUpdateSearch}
                      />
                    ))}
                  {searchType === searchTypesEnum.roles &&
                    selected.map((role) => (
                      <RoleCard
                        role={role as RoleType}
                        onUpdateSearch={onUpdateSearch}
                      />
                    ))}
                  {searchType === searchTypesEnum.tags &&
                    selected.map((tag) => (
                      <TagCard
                        tag={tag as TagType}
                        onUpdateSearch={onUpdateSearch}
                      />
                    ))}
                  {(searchType === searchTypesEnum.positions ||
                    searchType === searchTypesEnum.institutions) &&
                    selected.map((catalog) => (
                      <CatalogCard
                        item={catalog as CatalogSearchItem}
                        onUpdateSearch={onUpdateSearch}
                      />
                    ))}
                </>
              ) : (
                <div className="w-full min-h-[284px] flex justify-center items-center text-on-disabled">
                  {isFetching ? (
                    <Icon
                      type="far"
                      name="spinner-third"
                      className="text-on-disabled animate-spin text-2xl"
                    />
                  ) : (
                    <span className="text-sm leading-2xl font-normal font-geist text-center">{`Agrega ${getListType(searchType)} a esta lista para activar el filtro`}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const UserCard = ({
  user,
  onUpdateSearch,
}: {
  user: UserType;
  onUpdateSearch: (type: "remove" | "add", value: UserType) => void;
}) => {
  return (
    <div className="min-w-[332px] min-h-12 border border-neutral-quiet text-body bg-transparent py-2 px-3 rounded-lg">
      <div className="grid grid-cols-8">
        <div className="col-span-1 flex justify-start items-center">
          <Avatar size="xs">
            <AvatarFallback color="action">
              {user.name ? `${user.name.charAt(0)}` : ""}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="col-span-6 flex flex-col p-0 m-0 justify-start items-start">
          <span className="font-semibold span-0 m-0 text-base leading-3xl truncate max-w-full">{`${user.name} ${user.last_name}`}</span>
          <span className="span-0 m-0 text-sm font-normal leading-tight truncate max-w-full">
            {user.email}
          </span>
        </div>
        <div className="col-span-1 flex justify-end items-center">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Remove user"
            className="cursor-pointer p-0 m-0"
            onClick={() => onUpdateSearch("remove", user)}
          >
            <Icon
              type="far"
              name="xmark"
              className="font-normal text-body text-base leading-3xl"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};

const RoleCard = ({
  role,
  onUpdateSearch,
}: {
  role: RoleType;
  onUpdateSearch: (type: "remove" | "add", value: RoleType) => void;
}) => {
  return (
    <div className="min-w-[332px] min-h-12 border border-neutral-quiet text-body bg-transparent py-2 px-3 rounded-lg flex justify-between">
      <div className="w-4/5">
        <p className="font-semibold text-base leading-3xl truncate">{`${role.name}`}</p>
        <p className="text-sm font-normal leading-tight truncate">
          {role.description || "-"}
        </p>
      </div>
      <div className="flex justify-end items-center shrink-0">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Remover rol"
          className="cursor-pointer p-0 m-0"
          onClick={() => onUpdateSearch("remove", role)}
        >
          <Icon
            type="far"
            name="xmark"
            className="font-normal text-body text-base leading-3xl"
          />
        </Button>
      </div>
    </div>
  );
};

const TagCard = ({
  tag,
  onUpdateSearch,
}: {
  tag: TagType;
  onUpdateSearch: (type: "remove" | "add", value: TagType) => void;
}) => {
  const imageUrl = tag.files && tag.files.length > 0 ? tag.files[0].url : null;

  return (
    <div className="min-w-[332px] min-h-12 border border-neutral-quiet text-body bg-transparent py-2 px-3 rounded-lg flex justify-between">
      <div className=" w-full flex gap-2 items-center truncate">
        <Avatar size="xs" shape="square">
          <AvatarImage src={imageUrl || ""} alt={tag.name} />
          <AvatarFallback color="neutral" shape="square" type="icon">
            <Icon type="fas" name="tag" />
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 flex flex-col items-start text-base leading-3xl truncate">
          <span className="text-body font-semibold whitespace-nowrap">
            {tag.name}
          </span>
          <p className="text-body-quiet font-normal truncate text-xs">
            {tag.description || "-"}
          </p>
        </div>
      </div>
      <div className="flex justify-end items-center shrink-0">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Remover rol"
          className="cursor-pointer p-0 m-0"
          onClick={() => onUpdateSearch("remove", tag)}
        >
          <Icon
            type="far"
            name="xmark"
            className="font-normal text-body text-base leading-3xl"
          />
        </Button>
      </div>
    </div>
  );
};

const CatalogCard = ({
  item,
  onUpdateSearch,
}: {
  item: CatalogSearchItem;
  onUpdateSearch: (type: "remove" | "add", value: CatalogSearchItem) => void;
}) => {
  return (
    <div className="min-w-[332px] min-h-12 border border-neutral-quiet text-body bg-transparent py-2 px-3 rounded-lg flex justify-between">
      <div className="w-4/5">
        <p className="font-semibold text-base leading-3xl truncate">
          {item.name || item.nombre || "-"}
        </p>
        <p className="text-sm font-normal leading-tight truncate">
          {item.description || "-"}
        </p>
      </div>
      <div className="flex justify-end items-center shrink-0">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Remover elemento"
          className="cursor-pointer p-0 m-0"
          onClick={() => onUpdateSearch("remove", item)}
        >
          <Icon
            type="far"
            name="xmark"
            className="font-normal text-body text-base leading-3xl"
          />
        </Button>
      </div>
    </div>
  );
};
